import { useCallback, useEffect, useRef, useState } from "react";

export default function useJourneyAudio(allowed = true) {
  const engine = useRef(null);
  const voices = useRef([]);
  const [enabled, setEnabled] = useState(false);
  const stop = useCallback(() => {
    voices.current.forEach((node) => { try { node.stop(); } catch {} });
    voices.current = [];
    const current = engine.current;
    engine.current = null;
    if (current) current.close().catch(() => {});
    setEnabled(false);
  }, []);
  const toggle = useCallback(async () => {
    if (engine.current) { stop(); return; }
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!allowed || !AudioContext) return;
    try {
      const ctx = new AudioContext();
      engine.current = ctx;
      await ctx.resume();
      if (engine.current !== ctx) return;
      const buffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
      const channel = buffer.getChannelData(0);
      for (let i = 0; i < channel.length; i += 1) channel[i] = Math.random() * 2 - 1;
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass"; filter.frequency.value = 460;
      const gain = ctx.createGain(); gain.gain.value = .014;
      source.connect(filter).connect(gain).connect(ctx.destination);
      source.start();
      setEnabled(true);
    } catch { stop(); }
  }, [allowed, stop]);
  const cue = useCallback((destination) => {
    const ctx = engine.current;
    if (!ctx || ctx.state !== "running") return;
    voices.current.forEach((node) => { try { node.stop(); } catch {} });
    voices.current = [];
    destination.frequencies.forEach((frequency, i) => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      const start = ctx.currentTime + i * .065;
      oscillator.type = "sine"; oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(.0001, start);
      gain.gain.exponentialRampToValueAtTime(.022, start + .07);
      gain.gain.exponentialRampToValueAtTime(.0001, start + .75);
      oscillator.connect(gain);
      if (ctx.createStereoPanner) {
        const pan = ctx.createStereoPanner(); pan.pan.value = (destination.x - .5) * .6;
        gain.connect(pan).connect(ctx.destination);
      } else gain.connect(ctx.destination);
      oscillator.start(start); oscillator.stop(start + .8);
      voices.current.push(oscillator);
    });
  }, []);
  const step = useCallback(() => {
    const ctx = engine.current;
    if (!ctx || ctx.state !== "running") return;
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = "triangle"; oscillator.frequency.value = 65;
    gain.gain.setValueAtTime(.016, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(.0001, ctx.currentTime + .09);
    oscillator.connect(gain).connect(ctx.destination);
    oscillator.start(); oscillator.stop(ctx.currentTime + .1);
  }, []);
  useEffect(() => {
    const visibility = () => {
      const ctx = engine.current;
      if (!ctx || ctx.state === "closed") return;
      (document.hidden ? ctx.suspend() : ctx.resume()).catch(() => {});
    };
    document.addEventListener("visibilitychange", visibility);
    return () => { document.removeEventListener("visibilitychange", visibility); stop(); };
  }, [stop]);
  useEffect(() => { if (!allowed) stop(); }, [allowed, stop]);
  return { enabled, toggle, cue, step, stop };
}
